import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback, useId, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { ChevronDown, ChevronUp } from 'lucide-react';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
import ChartHelpButton from '../ChartHelpButton';
import { formatLargeNumber } from '../../utils/analyticsHelpers';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { usePersistWidgetSnapshot } from '../../hooks/usePersistWidgetSnapshot';
import { ChartTooltipFrame } from './ChartTooltipContent';
import { useChartTooltipTrigger } from './useChartTooltipTrigger';
import ChartFullscreenTitleControl from '../analytics/ChartFullscreenTitleControl';
import { PresetChartTitle, PresetFilterStrip } from '../analytics/PresetChartChrome';
import { useIsBelowLg, getResponsiveChartMargin } from '../../hooks/useMediaQuery';
import { singleSelectionLabel } from '../../../utils/filterLabels';
import {
  normalizeCountry,
  computeDestinationOrder,
  aggregateByVariety,
} from './cargoVarietiesAggregation';

const EAST_COLOR = '#3b82f6';
const WEST_COLOR = '#f59e0b';

// Backend period keys are: "3 days" | "7 days" | "10 days"
const PERIOD_OPTIONS = ['3 days', '7 days', '10 days'];
const DEFAULT_PERIOD = '10 days';

const MIN_ROW_HEIGHT = 28;
const MAX_ROW_HEIGHT = 52;
const Y_AXIS_WIDTH = 130;
// Fixed height reserved for the coast title above each panel's chart
const PANEL_HEADER_HEIGHT = 28;
// Same value as the portal tooltip in StackedBarChart.jsx (TOOLTIP_Z_INDEX) — keep in sync if that changes.
const TOOLTIP_Z_INDEX = 1000;
const TOOLTIP_VIEWPORT_PADDING_PX = 8;

const getAllOrigins = (origins = []) => {
  return [...new Set(origins.filter(Boolean))].sort();
};

/** Match API destination_country values that represent the United States */
const isUnitedStatesDestination = (country) => {
  const n = normalizeCountry(country);
  return (
    n === 'UNITED STATES'
    || n === 'USA'
    || n === 'US'
    || n === 'UNITED STATES OF AMERICA'
  );
};

const findUnitedStatesInList = (countries = []) =>
  countries.find((c) => isUnitedStatesDestination(c)) ?? null;

const getAllDestinationCountries = (destinationCountries = [], varieties = []) => {
  if (Array.isArray(destinationCountries) && destinationCountries.length > 0) {
    return [...new Set(destinationCountries.map(normalizeCountry).filter(Boolean))].sort();
  }
  return [...new Set(varieties.map((item) => normalizeCountry(item.destination_country)).filter(Boolean))].sort();
};

const hexToRgba = (hex, alpha) => {
  let h = String(hex || '').replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.substring(0, 2), 16) || 0;
  const g = parseInt(h.substring(2, 4), 16) || 0;
  const b = parseInt(h.substring(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Hatch direction for a non-primary destination index, as a 4-way cycle: "/" then "\" then
 * vertical then horizontal stripes, repeating from index 5 on. Returns both the CSS
 * `repeating-linear-gradient` angle and the matching SVG `<pattern patternTransform>` rotation
 * for the same index, so `getDestinationSwatchStyle` (CSS swatches/legend/tooltip) and the SVG
 * bar-fill `<pattern>` in CoastPanel can never drift out of sync — both call this one helper.
 * Not meaningful for index 0, which is always solid.
 */
const hatchForIndex = (index) => {
  switch ((index - 1) % 4) {
    case 0: return { cssAngle: 135, svgRotate: 45 }; // "/"
    case 1: return { cssAngle: 45, svgRotate: -45 }; // "\"
    case 2: return { cssAngle: 0, svgRotate: 0 }; // vertical stripes
    default: return { cssAngle: 90, svgRotate: 90 }; // horizontal stripes
  }
};

/** CSS fill for a destination swatch/segment. Index 0 (primary) is solid; others are hatched (see `hatchForIndex`). */
const getDestinationSwatchStyle = (index, color) => {
  if (index === 0) return { background: color };
  const { cssAngle } = hatchForIndex(index);
  return {
    background: hexToRgba(color, 0.22),
    backgroundImage: `repeating-linear-gradient(${cssAngle}deg, ${color} 0 2px, transparent 2px 6px)`,
  };
};

const CHARS_PER_LINE = 18;

const wrapVarietyName = (text, maxChars) => {
  const words = text.split(' ');
  const lines = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxChars) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines;
};

const VarietyTick = ({ x, y, payload, fill, fontSize: tickFontSize, rowHeight }) => {
  const fullName = payload?.value || '';
  const lines = wrapVarietyName(fullName, CHARS_PER_LINE);
  const lineHeight = (tickFontSize || 11) + 2;
  const totalTextHeight = lines.length * lineHeight;
  // Centre the block vertically within the row; fall back to simple offset for single lines.
  const startDy = rowHeight
    ? -(totalTextHeight / 2) + lineHeight * 0.8
    : lines.length > 1 ? -(totalTextHeight / 2) + lineHeight * 0.8 : 4;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        textAnchor="end"
        fill={fill}
        fontSize={tickFontSize}
      >
        {lines.map((line, i) => (
          <tspan key={i} x={0} dy={i === 0 ? startDy : lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

/**
 * Rendered in a portal to `document.body` (fixed positioning, tracking the pointer via
 * `coordinate` + `chartBoundaryRef`'s viewport rect) instead of Recharts' default in-chart
 * tooltip wrapper, so the split tooltip — taller than the single-destination one — is never
 * clipped by a card's `overflow: hidden` in the non-fullscreen widget layout.
 * In Recharts' vertical `layout="vertical"` bar chart, `coordinate` is the pointer's x paired
 * with the hovered ROW's centre y (not the raw cursor y) — so `py` below, and the vertical
 * offset derived from it, are relative to that row's centre.
 */
const CoastTooltip = memo(function CoastTooltip({ active, payload, label, coordinate, metric, coastColor, theme, isSplit, chartBoundaryRef }) {
  const { t } = useTranslation();
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState(null);

  const row = payload?.[0]?.payload;
  const isActive = Boolean(active && payload?.length && row);

  useLayoutEffect(() => {
    if (!isActive || !coordinate || !chartBoundaryRef?.current) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const boundaryEl = chartBoundaryRef.current;
      const tipEl = tooltipRef.current;
      if (!boundaryEl || !tipEl) return;

      const rect = boundaryEl.getBoundingClientRect();
      const px = rect.left + coordinate.x;
      const py = rect.top + coordinate.y;
      const width = tipEl.offsetWidth || 0;
      const height = tipEl.offsetHeight || 0;

      let left = px + 14;
      if (left + width > window.innerWidth - TOOLTIP_VIEWPORT_PADDING_PX) {
        left = px - 14 - width;
      }
      // Clamp horizontally too: the flip above only mirrors the tooltip to the other side of
      // the cursor, which can still leave it partly off-screen (e.g. a ~260px-wide split
      // tooltip near either edge of a narrow viewport).
      const maxLeft = window.innerWidth - width - TOOLTIP_VIEWPORT_PADDING_PX;
      left = maxLeft >= TOOLTIP_VIEWPORT_PADDING_PX
        ? Math.max(TOOLTIP_VIEWPORT_PADDING_PX, Math.min(left, maxLeft))
        : TOOLTIP_VIEWPORT_PADDING_PX; // Tooltip wider than the viewport allows even with padding on both sides.

      const maxTop = window.innerHeight - height - TOOLTIP_VIEWPORT_PADDING_PX;
      const top = Math.max(TOOLTIP_VIEWPORT_PADDING_PX, Math.min(py - 12, maxTop));

      setPosition({ left, top });
    };

    updatePosition();
    // Capturing listener on `document` (not just `window`) so a scroll on ANY ancestor
    // container re-positions the tooltip too — not just a window/page scroll. This matters in
    // the non-fullscreen widget layout (a card with its own scroll container) and in the
    // fullscreen analytics overlay (which scrolls an inner container, not the window).
    document.addEventListener('scroll', updatePosition, { capture: true, passive: true });
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('scroll', updatePosition, { capture: true });
      window.removeEventListener('resize', updatePosition);
    };
  }, [isActive, coordinate?.x, coordinate?.y, chartBoundaryRef, row]);

  if (!isActive) return null;

  const unit = metric === 'boxes' ? t('charts.cargoVarieties.boxes') : 'kg';
  const hasSplit = isSplit && (row.destinations?.length || 0) > 1;

  return createPortal(
    <div
      ref={tooltipRef}
      style={{
        position: 'fixed',
        left: position?.left ?? -9999,
        top: position?.top ?? -9999,
        zIndex: TOOLTIP_Z_INDEX,
        pointerEvents: 'none',
        opacity: position ? 1 : 0,
      }}
    >
    <ChartTooltipFrame theme={theme} className="min-w-[180px] max-w-[260px]">
      <p className="mb-1 text-xs font-semibold" style={{ color: theme.tooltipText }}>{label}</p>
      {row.type_of_variety && row.type_of_variety.toLowerCase() !== 'n/a' && (
        <p className="mb-2 text-xs" style={{ color: theme.tooltipSecondaryText }}>{row.type_of_variety}</p>
      )}
      <p className="mb-1 text-xs font-medium" style={{ color: coastColor }}>
        {formatLargeNumber(row.value)} {unit}
      </p>
      {hasSplit ? (
        <div className="mt-1 border-t pt-1" style={{ borderColor: theme.tooltipBorder }}>
          {row.destinations.map((d) => (
            <div key={d.key} className="mt-1.5 first:mt-0">
              <div className="flex items-center justify-between gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <span
                    className="inline-block flex-shrink-0"
                    style={{ width: 10, height: 10, borderRadius: 2, ...getDestinationSwatchStyle(d.index, coastColor) }}
                  />
                  <span style={{ color: theme.tooltipText }}>{d.label}</span>
                </span>
                <span style={{ color: theme.tooltipSecondaryText }}>
                  {formatLargeNumber(d.value)} {unit} · {d.pct.toFixed(1)}%
                </span>
              </div>
              {Object.keys(d.ports || {}).length > 0 && (
                <div className="pl-4">
                  {Object.entries(d.ports)
                    .sort(([, a], [, b]) => (b[metric] || 0) - (a[metric] || 0))
                    .map(([port, vals]) => (
                      <div key={port} className="mt-0.5 flex justify-between gap-3 text-xs">
                        <span style={{ color: theme.tooltipSecondaryText }}>{port}</span>
                        <span style={{ color: theme.tooltipText }}>{formatLargeNumber(vals[metric] || 0)} {unit}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        Object.keys(row.ports || {}).length > 0 && (
          <div className="mt-1 border-t pt-1" style={{ borderColor: theme.tooltipBorder }}>
            {Object.entries(row.ports)
              .sort(([, a], [, b]) => (b[metric] || 0) - (a[metric] || 0))
              .map(([port, vals]) => (
                <div key={port} className="mt-0.5 flex justify-between gap-3 text-xs">
                  <span style={{ color: theme.tooltipSecondaryText }}>{port}</span>
                  <span style={{ color: theme.tooltipText }}>{formatLargeNumber(vals[metric] || 0)} {unit}</span>
                </div>
              ))}
          </div>
        )
      )}
    </ChartTooltipFrame>
    </div>,
    document.body
  );
});

const CoastPanel = ({
  title,
  totalLabel,
  rows,
  barColor,
  metric,
  theme,
  fontSize,
  containerHeight,
  xAxisLabel,
  noDataMessage,
  destinationSeries,
  isSplit,
  coastKey,
  patternIdPrefix,
}) => {
  const { t } = useTranslation();
  const chartTooltipBoundaryRef = useRef(null);
  const { trigger: tooltipTrigger, tooltipDismissKey } = useChartTooltipTrigger(chartTooltipBoundaryRef);
  const isBelowLg = useIsBelowLg();
  const barCursor = useMemo(() => ({ fill: `${theme.gridColor}55` }), [theme.gridColor]);

  const maxVal = rows.reduce((acc, r) => Math.max(acc, r.value), 0);
  const domainMax = Math.ceil(maxVal * 1.15);
  // The per-destination summary now renders inline in the header row itself (see below)
  // instead of a separate row, so both panels share the same fixed header height with no
  // extra space to reserve — bar rows stay aligned by construction.
  const headerHeight = PANEL_HEADER_HEIGHT;

  // Space consumed by the X-axis ticks and label below the bars.
  const X_AXIS_SPACE = 36;

  // Compute the tallest label (in lines) so the row height always fits the full name.
  const tickFontSize = fontSize - 3;
  const lineHeight = tickFontSize + 2;
  const maxLabelLines = rows.reduce((max, r) => {
    const lines = wrapVarietyName(r.variety || '', CHARS_PER_LINE);
    return Math.max(max, lines.length);
  }, 1);
  const labelMinRowHeight = Math.max(MIN_ROW_HEIGHT, maxLabelLines * lineHeight + 6);

  // Derive row height from the measured container so bars fill the available space
  // instead of overflowing or leaving empty gaps when the widget is resized vertically.
  const chartBodyHeight = containerHeight
    ? Math.max(containerHeight - headerHeight - X_AXIS_SPACE, rows.length * labelMinRowHeight)
    : rows.length * 30;

  const rowHeight = rows.length > 0
    ? Math.max(labelMinRowHeight, Math.min(MAX_ROW_HEIGHT, Math.floor(chartBodyHeight / rows.length)))
    : 30;

  const chartHeight = (rows.length + 1) * rowHeight + X_AXIS_SPACE;
  const barSize = Math.max(6, Math.min(Math.floor(rowHeight * 0.52), 28));

  // Only show a scrollbar when the minimum row height causes the chart to exceed the container.
  const needsScroll = containerHeight ? chartHeight > containerHeight - headerHeight : false;

  return (
    <div className="flex flex-col min-w-0 h-full">
      {/* Coast title + total, and — when split — the per-destination summary (swatch, label,
          pct) inline in the same row, separated by a middle dot. This used to be its own row
          (removed DestinationSummaryLine component) with a matching empty-spacer row on the
          unsplit sibling panel to keep bar rows aligned; now both panels share one fixed-height
          header with nothing to reserve, so they're aligned by construction. Absolute values
          are omitted here to keep the line short — they're still available in the bar tooltip.
          The summary is a non-shrinking flex item (`flex: '0 0 auto'`); the title is the one
          `minWidth: 0` + `truncate` item, so on a narrow panel the TITLE truncates first rather
          than the destination summary silently clipping mid-item with no ellipsis (`text-overflow`
          has no effect on flex children, only on a single run of inline/block content). */}
      <p
        className="px-2 flex-shrink-0"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          height: PANEL_HEADER_HEIGHT,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <span className="text-xs font-semibold truncate" style={{ color: barColor, minWidth: 0 }}>
          {title}
          <span className="font-normal ml-1" style={{ opacity: 0.85 }}>({totalLabel})</span>
        </span>
        {isSplit && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flex: '0 0 auto',
              fontSize: fontSize - 3,
            }}
          >
            {/* Accessible name for this region instead of `aria-label` (which would replace,
                not prefix, the visible swatch/label/pct text read out by screen readers). */}
            <span className="sr-only">{t('charts.cargoVarieties.byDestination')}: </span>
            <span aria-hidden="true" style={{ color: theme.secondaryText, flexShrink: 0 }}>&middot;</span>
            {destinationSeries.map((series) => (
              <span key={series.key} className="inline-flex items-center gap-1 flex-shrink-0">
                <span
                  className="inline-block flex-shrink-0"
                  style={{ width: 10, height: 10, borderRadius: 2, ...getDestinationSwatchStyle(series.index, barColor) }}
                />
                <span style={{ color: theme.tickFill }}>{series.label}</span>
                <span style={{ color: theme.secondaryText }}>{series.pct.toFixed(1)}%</span>
              </span>
            ))}
          </span>
        )}
      </p>

      <div
        className="flex-1 min-h-0 overflow-x-hidden"
        style={{ overflowY: needsScroll ? 'auto' : 'hidden' }}
      >
        {rows.length === 0 ? (
          <div className="h-full flex items-center justify-center px-3 text-center">
            <p className="text-xs" style={{ color: theme.noDataText }}>{noDataMessage}</p>
          </div>
        ) : (
        <div ref={chartTooltipBoundaryRef} style={{ width: '100%', height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              layout="vertical"
              margin={getResponsiveChartMargin({ isBelowLg })}
              barSize={barSize}
            >
              <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke={theme.gridColor} />
              <XAxis
                type="number"
                domain={[0, domainMax]}
                tickFormatter={(v) => formatLargeNumber(v)}
                tick={{ fill: theme.axisLabelFill, fontSize: fontSize - 3 }}
                axisLine={{ stroke: theme.axisStroke }}
                tickLine={{ stroke: theme.axisStroke }}
                label={{
                  value: xAxisLabel,
                  position: 'insideBottom',
                  offset: -8,
                  fill: theme.axisLabelFill,
                  fontSize: fontSize - 3,
                }}
              />
              <YAxis
                type="category"
                dataKey="variety"
                width={Y_AXIS_WIDTH}
                interval={0}
                tick={<VarietyTick fill={theme.tickFill} fontSize={fontSize - 3} rowHeight={rowHeight} />}
                axisLine={false}
                tickLine={false}
              />
              {/* Recharts keeps its own tooltip wrapper mounted (needed for hover/trigger
                  handling and positioning math) but invisible, via wrapperStyle +
                  allowEscapeViewBox; CoastTooltip renders the actual visible tooltip itself,
                  in a portal to document.body. */}
              <Tooltip
                key={tooltipDismissKey}
                trigger={tooltipTrigger}
                throttleDelay={50}
                cursor={barCursor}
                wrapperStyle={{ visibility: 'hidden', pointerEvents: 'none' }}
                allowEscapeViewBox={{ x: true, y: true }}
                content={(tooltipProps) => (
                  <CoastTooltip
                    {...tooltipProps}
                    chartBoundaryRef={chartTooltipBoundaryRef}
                    metric={metric}
                    coastColor={barColor}
                    theme={theme}
                    isSplit={isSplit}
                  />
                )}
              />
              {/* Mirrors getDestinationSwatchStyle's CSS hatch via the same `hatchForIndex`
                  helper (same index -> same stripe direction), so a bar segment's fill always
                  matches its legend/tooltip swatch. */}
              {isSplit && (
                <defs>
                  {destinationSeries
                    .filter((series) => series.index >= 1)
                    .map((series) => {
                      const { svgRotate } = hatchForIndex(series.index);
                      return (
                        <pattern
                          key={series.key}
                          id={`${patternIdPrefix}-${coastKey}-${series.index}`}
                          patternUnits="userSpaceOnUse"
                          width="6"
                          height="6"
                          patternTransform={`rotate(${svgRotate})`}
                        >
                          <rect width="6" height="6" fill={barColor} fillOpacity="0.22" />
                          {/* Centred on the tile (x=3, not x=0) so the stroke isn't clipped at the tile edge. */}
                          <line x1="3" y1="0" x2="3" y2="6" stroke={barColor} strokeWidth="2" />
                        </pattern>
                      );
                    })}
                </defs>
              )}
              {isSplit ? (
                destinationSeries.map((series, i) => {
                  const isLast = i === destinationSeries.length - 1;
                  const isPrimary = series.index === 0;
                  return (
                    <Bar
                      key={series.key}
                      dataKey={`seg_${series.index}`}
                      stackId="coast"
                      fill={isPrimary ? barColor : `url(#${patternIdPrefix}-${coastKey}-${series.index})`}
                      stroke={isPrimary ? undefined : theme.tooltipBg}
                      strokeWidth={isPrimary ? undefined : 1}
                      radius={[0, 0, 0, 0]}
                      isAnimationActive={false}
                    >
                      {isLast && (
                        <LabelList
                          dataKey="value"
                          position="right"
                          formatter={(v) => formatLargeNumber(v)}
                          style={{ fill: theme.axisLabelFill, fontSize: fontSize - 3 }}
                        />
                      )}
                    </Bar>
                  );
                })
              ) : (
                <Bar dataKey="value" fill={barColor} radius={[0, 3, 3, 0]} isAnimationActive={false}>
                  <LabelList
                    dataKey="value"
                    position="right"
                    formatter={(v) => formatLargeNumber(v)}
                    style={{ fill: theme.axisLabelFill, fontSize: fontSize - 3 }}
                  />
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
        )}
      </div>
    </div>
  );
};

const CargoVarietiesChart = ({
  periods,
  available,
  availableCommodities = [],
  unavailableMessage,
  forceFiltersOpen = false,
  presetDescription = '',
  analyticsFullscreen = null,
  presetSingleChartFullscreen = false,
  hideHelpButton = false,
}) => {
  const { t } = useTranslation();
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  const presetEmbedded = forceFiltersOpen && !presetSingleChartFullscreen;
  // Single-chart fullscreen: below lg the overlay is the only scroll container,
  // so these boxes grow with their content (both coast panels in full) instead
  // of being squeezed. See the `.preset-fullscreen-*` block in index.css.
  const fsFill = presetSingleChartFullscreen ? ' preset-fullscreen-fill' : '';

  const [selectedPeriod, setSelectedPeriod] = useState(DEFAULT_PERIOD);
  const [selectedMetric, setSelectedMetric] = useState('weight_kg');
  const [selectedCoasts, setSelectedCoasts] = useState(['west', 'east']);
  const [coastsUserCleared, setCoastsUserCleared] = useState(false);
  const [selectedOrigins, setSelectedOrigins] = useState([]);
  const [selectedDestinationCountries, setSelectedDestinationCountries] = useState([]);
  // Collapsed by default. The preset view keeps filters open from lg up via the
  // CSS gate on the strip below; smaller screens stay behind the Filters toggle.
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Modal states
  const [originModalOpen, setOriginModalOpen] = useState(false);
  const [destinationCountryModalOpen, setDestinationCountryModalOpen] = useState(false);
  const [coastModalOpen, setCoastModalOpen] = useState(false);
  const [periodModalOpen, setPeriodModalOpen] = useState(false);
  const [metricModalOpen, setMetricModalOpen] = useState(false);

  const panelsRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(0);
  // Unique per chart instance so two chart instances on one page never collide on pattern ids.
  // React's generated ids contain delimiter characters (":r0:" pre-19; a different delimiter in
  // React 19+) that aren't valid inside `url(#...)`, so strip everything but safe id characters.
  const patternIdPrefix = useId().replace(/[^A-Za-z0-9_-]/g, '');

  const hydrateCargo = useCallback((s) => {
    if (s.selectedPeriod && PERIOD_OPTIONS.includes(s.selectedPeriod)) setSelectedPeriod(s.selectedPeriod);
    if (s.selectedMetric === 'boxes' || s.selectedMetric === 'kg') setSelectedMetric(s.selectedMetric);
    if (Array.isArray(s.selectedCoasts) && s.selectedCoasts.length) {
      const next = s.selectedCoasts.filter((c) => c === 'east' || c === 'west');
      if (next.length) setSelectedCoasts(next);
    }
    if (typeof s.filtersExpanded === 'boolean') setFiltersExpanded(s.filtersExpanded);
  }, []);

  const snapshotCargo = useCallback(
    () => ({
      selectedPeriod,
      selectedMetric,
      selectedCoasts,
      filtersExpanded,
    }),
    [selectedPeriod, selectedMetric, selectedCoasts, filtersExpanded]
  );

  usePersistWidgetSnapshot(
    'cargo-varieties',
    periods,
    !!periods,
    hydrateCargo,
    snapshotCargo,
    [selectedPeriod, selectedMetric, selectedCoasts, filtersExpanded]
  );

  useEffect(() => {
    const el = panelsRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setPanelHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!periods || typeof periods !== 'object') return;

    const hasSelected = Object.prototype.hasOwnProperty.call(periods, selectedPeriod);
    if (hasSelected) return;

    const candidates = [
      DEFAULT_PERIOD,
      '7 days',
      ...PERIOD_OPTIONS,
      ...Object.keys(periods),
    ];

    const next = candidates.find((k) => Object.prototype.hasOwnProperty.call(periods, k));
    if (next && next !== selectedPeriod) {
      setSelectedPeriod(next);
    }
  }, [periods, selectedPeriod]);

  const period = periods?.[selectedPeriod];
  const allOrigins = useMemo(
    () => getAllOrigins(period?.origins || []),
    [period]
  );

  const varietiesForSelectedOrigins = useMemo(() => {
    const varieties = period?.varieties || [];
    if (selectedOrigins.length === 0) return [];
    return varieties.filter((v) => selectedOrigins.includes(v.origin));
  }, [period, selectedOrigins]);

  /** Destination countries that exist for the currently selected origin(s) — cascades from origin */
  const destinationCountriesForOrigins = useMemo(
    () => getAllDestinationCountries([], varietiesForSelectedOrigins),
    [varietiesForSelectedOrigins]
  );

  useEffect(() => {
    if (allOrigins.length === 0) {
      setSelectedOrigins([]);
      return;
    }
    setSelectedOrigins((prev) => {
      const validSelections = prev.filter((origin) => allOrigins.includes(origin));
      return validSelections.length > 0 ? validSelections : allOrigins;
    });
  }, [allOrigins]);

  useEffect(() => {
    if (destinationCountriesForOrigins.length === 0) {
      setSelectedDestinationCountries([]);
      return;
    }
    setSelectedDestinationCountries((prev) => {
      const valid = prev.filter((country) => destinationCountriesForOrigins.includes(country));
      if (valid.length > 0) return valid;
      return [...destinationCountriesForOrigins];
    });
  }, [destinationCountriesForOrigins]);

  const originFilteredVarieties = useMemo(() => {
    const varieties = period?.varieties || [];
    if (selectedOrigins.length === 0 || selectedDestinationCountries.length === 0) return [];
    return varieties.filter(
      (v) => selectedOrigins.includes(v.origin) && selectedDestinationCountries.includes(normalizeCountry(v.destination_country))
    );
  }, [period, selectedOrigins, selectedDestinationCountries]);

  /** Coasts that have volume for the current origin + destination + unit — cascades after destination */
  const availableCoastKeys = useMemo(() => {
    if (selectedOrigins.length === 0 || selectedDestinationCountries.length === 0) {
      return ['west', 'east'];
    }
    const m = selectedMetric;
    const hasWest = originFilteredVarieties.some((v) => (v.west_coast?.[m] || 0) > 0);
    const hasEast = originFilteredVarieties.some((v) => (v.east_coast?.[m] || 0) > 0);
    if (!hasWest && !hasEast) return ['west', 'east'];
    const keys = [];
    if (hasWest) keys.push('west');
    if (hasEast) keys.push('east');
    return keys;
  }, [originFilteredVarieties, selectedMetric]);

  useEffect(() => {
    setSelectedCoasts((prev) => {
      if (coastsUserCleared && prev.length === 0) return [];
      const valid = prev.filter((k) => availableCoastKeys.includes(k));
      if (valid.length > 0) return valid;
      return availableCoastKeys.length > 0 ? [...availableCoastKeys] : ['west', 'east'];
    });
  }, [availableCoastKeys, coastsUserCleared]);

  // Global destination order (across both coasts) so texture (solid vs hatched) follows
  // the destination entity itself, not its rank within a single coast panel.
  const destinationOrder = useMemo(
    () => computeDestinationOrder(originFilteredVarieties, selectedMetric),
    [originFilteredVarieties, selectedMetric]
  );

  const eastRows = useMemo(
    () => aggregateByVariety(originFilteredVarieties, 'east_coast', selectedMetric, destinationOrder),
    [originFilteredVarieties, selectedMetric, destinationOrder]
  );
  const westRows = useMemo(
    () => aggregateByVariety(originFilteredVarieties, 'west_coast', selectedMetric, destinationOrder),
    [originFilteredVarieties, selectedMetric, destinationOrder]
  );

  const helpDescription = t('charts.cargoVarieties.helpText');

  const coastDisplayLabel =
    selectedCoasts.length === 0
      ? t('charts.cargoVarieties.selectCoast', 'Select coast')
      : selectedCoasts.length === 2
        ? t('charts.cargoVarieties.bothCoasts')
        : selectedCoasts.includes('east')
          ? t('charts.cargoVarieties.eastCoast')
          : t('charts.cargoVarieties.westCoast');

  const metricDisplayLabel = selectedMetric === 'boxes' ? t('charts.cargoVarieties.boxes') : t('charts.cargoVarieties.kg');
  const originDisplayLabel = selectedOrigins.length === 0
    ? t('filters.selectOrigins')
    : singleSelectionLabel(selectedOrigins, allOrigins)
      ?? (selectedOrigins.length === allOrigins.length
        ? t('filters.allOrigins')
        : t('filters.nSelected', { count: selectedOrigins.length }));
  const destinationCountryDisplayLabel = selectedDestinationCountries.length === 0
    ? t('charts.cargoVarieties.selectDestinationCountry')
    : singleSelectionLabel(selectedDestinationCountries, destinationCountriesForOrigins)
      ?? (selectedDestinationCountries.length === destinationCountriesForOrigins.length
        ? t('charts.cargoVarieties.allDestinations')
        : t('filters.nSelected', { count: selectedDestinationCountries.length }));

  const xAxisLabel = selectedMetric === 'boxes'
    ? t('charts.cargoVarieties.xAxisBoxes')
    : t('charts.cargoVarieties.xAxisKg');

  const coastLabelToKey = {
    [t('charts.cargoVarieties.eastCoast')]: 'east',
    [t('charts.cargoVarieties.westCoast')]: 'west',
  };
  const coastKeyToLabel = {
    east: t('charts.cargoVarieties.eastCoast'),
    west: t('charts.cargoVarieties.westCoast'),
  };
  const coastModalOptionLabels = availableCoastKeys.map((k) => coastKeyToLabel[k]);

  const handleCoastSelect = (label) => {
    const key = coastLabelToKey[label];
    if (!key) return;
    if (selectedCoasts.includes(key)) {
      // Prevent deselecting the last item
      if (selectedCoasts.length > 1) {
        setSelectedCoasts(selectedCoasts.filter((c) => c !== key));
      }
    } else {
      setSelectedCoasts([...selectedCoasts, key]);
    }
  };

  const handleOriginSelect = (origin) => {
    if (selectedOrigins.includes(origin)) {
      setSelectedOrigins(selectedOrigins.filter((o) => o !== origin));
    } else {
      setSelectedOrigins([...selectedOrigins, origin]);
    }
  };
  const handleDestinationCountrySelect = (country) => {
    if (selectedDestinationCountries.includes(country)) {
      setSelectedDestinationCountries(selectedDestinationCountries.filter((c) => c !== country));
    } else {
      setSelectedDestinationCountries([...selectedDestinationCountries, country]);
    }
  };

  const titleBar = (
    <div className={forceFiltersOpen ? 'px-4 pt-4' : 'px-4'}>
    <PresetChartTitle
      forceFiltersOpen={forceFiltersOpen}
      title={`${t('charts.cargoVarieties.titleSuffix')} - ${selectedPeriod.toUpperCase()}`}
      theme={theme}
      fontSize={fontSize}
      titleAfter={
        !hideHelpButton ? (
          <ChartHelpButton
            title={t('charts.cargoVarieties.helpTitle')}
            description={helpDescription}
          />
        ) : null
      }
      right={
        <ChartFullscreenTitleControl
          analyticsFullscreen={analyticsFullscreen}
          presetSingleChartFullscreen={presetSingleChartFullscreen}
        />
      }
    />
    </div>
  );

  if (available === false) {
    return (
      <div className={(presetEmbedded ? 'w-full min-h-0' : 'h-full') + ' flex flex-col bg-transparent'}>
        {titleBar}
        <div
          className={
            (presetEmbedded ? 'py-16' : 'flex-1') +
            ' flex flex-col items-center justify-center gap-3 px-6 text-center'
          }
        >
          <p className="text-sm font-medium" style={{ color: theme.noDataText }}>
            {unavailableMessage || t('charts.cargoVarieties.unavailable')}
          </p>
          {availableCommodities.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center mt-1">
              {availableCommodities.map((c) => (
                <span
                  key={c}
                  className="px-2 py-0.5 rounded text-xs font-medium"
                  style={{ background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, color: theme.tickFill }}
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!period) {
    return (
      <div className={(presetEmbedded ? 'w-full min-h-0' : 'h-full') + ' flex flex-col bg-transparent'}>
        {titleBar}
        <div className={(presetEmbedded ? 'py-16' : 'flex-1') + ' flex items-center justify-center'}>
          <p className="text-sm" style={{ color: theme.noDataText }}>{t('charts.cargoVarieties.noData')}</p>
        </div>
      </div>
    );
  }

  const totals = originFilteredVarieties.reduce((acc, varietyItem) => {
    const eastValue = varietyItem.east_coast?.[selectedMetric] || 0;
    const westValue = varietyItem.west_coast?.[selectedMetric] || 0;
    acc.east += eastValue;
    acc.west += westValue;
    return acc;
  }, { east: 0, west: 0 });
  const metricUnit = selectedMetric === 'boxes' ? t('charts.cargoVarieties.boxes') : 'kg';
  const eastTotal = totals.east;
  const westTotal = totals.west;
  const grandTotal = eastTotal + westTotal;
  const eastPct = grandTotal > 0 ? (eastTotal / grandTotal) * 100 : 0;
  const westPct = grandTotal > 0 ? (westTotal / grandTotal) * 100 : 0;

  const showEast = selectedCoasts.includes('east');
  const showWest = selectedCoasts.includes('west');

  const anySplit = (showWest && westRows.isSplit) || (showEast && eastRows.isSplit);
  const visibleDestinationSeriesMap = new Map();
  // Only collect from panels that are actually split — a destination present only on an
  // unsplit panel (which renders a solid bar) must not pick up a hatched legend swatch.
  if (showWest && westRows.isSplit) westRows.destinationSeries.forEach((s) => { if (!visibleDestinationSeriesMap.has(s.key)) visibleDestinationSeriesMap.set(s.key, s); });
  if (showEast && eastRows.isSplit) eastRows.destinationSeries.forEach((s) => { if (!visibleDestinationSeriesMap.has(s.key)) visibleDestinationSeriesMap.set(s.key, s); });
  const visibleDestinationSeries = [...visibleDestinationSeriesMap.values()].sort((a, b) => a.index - b.index);

  const noSelectionMessage = selectedOrigins.length === 0
    ? t('charts.selectAtLeastOneRegion')
    : t('charts.cargoVarieties.selectAtLeastOneDestinationCountry');

  return (
    <div
      className={(presetEmbedded ? 'w-full min-h-0' : 'h-full' + fsFill) + ' flex flex-col bg-transparent'}
      style={{ color: theme.tickFill }}
    >
      {/* Title */}
      {titleBar}

      <div
        className={
          (presetEmbedded ? '' : 'flex-1' + fsFill + ' ') + 'flex flex-col min-h-0 px-4'
        }
      >
        {/* Filters */}
        {/* Collapsible in the widget dashboard, and below lg in the preset view
            too; lg:hidden once forceFiltersOpen since desktop preset keeps the
            strip open via the CSS gate below. */}
        <button
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className={`w-full flex items-center justify-between py-2 border-b flex-shrink-0 ${forceFiltersOpen ? 'lg:hidden' : ''}`}
          style={{ borderColor: theme.borderSecondary }}
        >
          <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>{t('charts.cargoVarieties.filters')}</span>
          {filtersExpanded ? <ChevronUp size={16} style={{ color: theme.secondaryText }} /> : <ChevronDown size={16} style={{ color: theme.secondaryText }} />}
        </button>

        {(forceFiltersOpen || filtersExpanded) && (
          <div
            className={`py-2 space-y-2 flex-shrink-0${
              forceFiltersOpen ? (filtersExpanded ? ' max-lg:pt-3' : ' max-lg:hidden') : ''
            }`}
          >
            {forceFiltersOpen ? (
              <PresetFilterStrip
                forceFiltersOpen
                presetSingleChartFullscreen={presetSingleChartFullscreen}
                analyticsFullscreen={analyticsFullscreen}
                titleHasFullscreenControl
              >
                <FilterDropdown
                  label={t('charts.cargoVarieties.origin')}
                  value={originDisplayLabel}
                  onClick={() => setOriginModalOpen(true)}
                  compact
                />
                <FilterDropdown
                  label={t('charts.cargoVarieties.destinationCountry')}
                  value={destinationCountryDisplayLabel}
                  onClick={() => setDestinationCountryModalOpen(true)}
                  compact
                />
                <FilterDropdown
                  label={t('charts.cargoVarieties.coast')}
                  value={coastDisplayLabel}
                  onClick={() => setCoastModalOpen(true)}
                  compact
                />
                <FilterDropdown
                  label={t('charts.cargoVarieties.period')}
                  value={selectedPeriod}
                  onClick={() => setPeriodModalOpen(true)}
                  compact
                />
                <FilterDropdown
                  label={t('charts.cargoVarieties.unit')}
                  value={metricDisplayLabel}
                  onClick={() => setMetricModalOpen(true)}
                  compact
                />
              </PresetFilterStrip>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <FilterDropdown
                    label={t('charts.cargoVarieties.origin')}
                    value={originDisplayLabel}
                    onClick={() => setOriginModalOpen(true)}
                  />
                  <FilterDropdown
                    label={t('charts.cargoVarieties.destinationCountry')}
                    value={destinationCountryDisplayLabel}
                    onClick={() => setDestinationCountryModalOpen(true)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <FilterDropdown
                    label={t('charts.cargoVarieties.coast')}
                    value={coastDisplayLabel}
                    onClick={() => setCoastModalOpen(true)}
                  />
                  <FilterDropdown
                    label={t('charts.cargoVarieties.period')}
                    value={selectedPeriod}
                    onClick={() => setPeriodModalOpen(true)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <FilterDropdown
                      label={t('charts.cargoVarieties.unit')}
                      value={metricDisplayLabel}
                      onClick={() => setMetricModalOpen(true)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {selectedOrigins.length === 0 || selectedDestinationCountries.length === 0 ? (
          <div
            className={
              (presetEmbedded ? 'flex py-12' : 'flex flex-1 min-h-0') +
              ' items-center justify-center'
            }
          >
            <p className="text-sm text-center px-4" style={{ color: theme.secondaryText }}>
              {noSelectionMessage}
            </p>
          </div>
        ) : (
          <>
            {/* Total scorecard */}
            <div className="flex items-center justify-center gap-3 py-1.5 flex-shrink-0">
              <span className="text-xs font-bold" style={{ color: theme.tickFill }}>
                {t('charts.cargoVarieties.total')}: {formatLargeNumber(grandTotal)} {metricUnit}
              </span>
            </div>

            {/* Chart panels */}
            <div
              ref={panelsRef}
              className={
                (presetEmbedded
                  ? 'flex w-full gap-0 overflow-x-hidden'
                  : 'flex flex-1 min-h-0 gap-0 overflow-hidden' + fsFill)
              }
            >
              {showWest && (
                <div
                  className={
                    'flex flex-col flex-1 min-w-0 px-1 pb-1' +
                    (presetEmbedded ? '' : ' overflow-hidden h-full' + fsFill)
                  }
                >
                  <CoastPanel
                    title={`${t('charts.cargoVarieties.westCoast')} — ${formatLargeNumber(westTotal)} ${metricUnit}`}
                    totalLabel={`${westPct.toFixed(1)}%`}
                    rows={westRows.rows}
                    destinationSeries={westRows.destinationSeries}
                    isSplit={westRows.isSplit}
                    coastKey="west"
                    patternIdPrefix={patternIdPrefix}
                    barColor={WEST_COLOR}
                    metric={selectedMetric}
                    theme={theme}
                    fontSize={fontSize}
                    containerHeight={panelHeight}
                    xAxisLabel={xAxisLabel}
                    noDataMessage={t('charts.cargoVarieties.noDataForCoast', { coast: t('charts.cargoVarieties.westCoast') })}
                  />
                </div>
              )}

              {showEast && showWest && (
                <div className="w-px flex-shrink-0" style={{ background: theme.borderSecondary }} />
              )}

              {showEast && (
                <div
                  className={
                    'flex flex-col flex-1 min-w-0 px-1 pb-1' +
                    (presetEmbedded ? '' : ' overflow-hidden h-full' + fsFill)
                  }
                >
                  <CoastPanel
                    title={`${t('charts.cargoVarieties.eastCoast')} — ${formatLargeNumber(eastTotal)} ${metricUnit}`}
                    totalLabel={`${eastPct.toFixed(1)}%`}
                    rows={eastRows.rows}
                    destinationSeries={eastRows.destinationSeries}
                    isSplit={eastRows.isSplit}
                    coastKey="east"
                    patternIdPrefix={patternIdPrefix}
                    barColor={EAST_COLOR}
                    metric={selectedMetric}
                    theme={theme}
                    fontSize={fontSize}
                    containerHeight={panelHeight}
                    xAxisLabel={xAxisLabel}
                    noDataMessage={t('charts.cargoVarieties.noDataForCoast', { coast: t('charts.cargoVarieties.eastCoast') })}
                  />
                </div>
              )}
            </div>

            {/* Legend */}
            <div className={`flex items-center justify-center gap-4 ${forceFiltersOpen && !presetSingleChartFullscreen ? 'pt-1' : 'py-1'} flex-shrink-0 border-t`} style={{ fontSize: fontSize - 2, borderColor: theme.borderSecondary }}>
              {showWest && (
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: WEST_COLOR }} />
                  <span style={{ color: theme.legendText }}>{t('charts.cargoVarieties.westCoast')}</span>
                </span>
              )}
              {showEast && (
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: EAST_COLOR }} />
                  <span style={{ color: theme.legendText }}>{t('charts.cargoVarieties.eastCoast')}</span>
                </span>
              )}
              {anySplit && (
                <>
                  <span className="w-px h-3.5" style={{ background: theme.borderSecondary }} />
                  {visibleDestinationSeries.map((series) => (
                    <span key={series.key} className="flex items-center gap-1">
                      <span
                        className="inline-block flex-shrink-0 rounded-sm"
                        style={{ width: 14, height: 10, ...getDestinationSwatchStyle(series.index, theme.tickFill) }}
                      />
                      <span style={{ color: theme.legendText }}>{series.label}</span>
                    </span>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Filter Modals */}
      <FilterModal
        isOpen={originModalOpen}
        onClose={() => setOriginModalOpen(false)}
        title={t('charts.cargoVarieties.selectOrigin')}
        options={allOrigins}
        selectedValues={selectedOrigins}
        onSelect={handleOriginSelect}
        multiSelect
        onSelectAll={() => setSelectedOrigins(allOrigins)}
        onClearAll={() => setSelectedOrigins([])}
      />
      <FilterModal
        isOpen={destinationCountryModalOpen}
        onClose={() => setDestinationCountryModalOpen(false)}
        title={t('charts.cargoVarieties.selectDestinationCountry')}
        options={destinationCountriesForOrigins}
        selectedValues={selectedDestinationCountries}
        onSelect={handleDestinationCountrySelect}
        multiSelect
        onSelectAll={() => setSelectedDestinationCountries(destinationCountriesForOrigins)}
        onClearAll={() => setSelectedDestinationCountries([])}
      />

      <FilterModal
        isOpen={coastModalOpen}
        onClose={() => setCoastModalOpen(false)}
        title={t('charts.cargoVarieties.selectCoast')}
        options={coastModalOptionLabels}
        selectedValues={selectedCoasts.map((k) => coastKeyToLabel[k])}
        onSelect={handleCoastSelect}
        multiSelect
        onSelectAll={() => {
          setSelectedCoasts([...availableCoastKeys]);
          setCoastsUserCleared(false);
        }}
        onClearAll={() => {
          setSelectedCoasts([]);
          setCoastsUserCleared(true);
        }}
      />

      <FilterModal
        isOpen={periodModalOpen}
        onClose={() => setPeriodModalOpen(false)}
        title={t('charts.cargoVarieties.selectPeriod')}
        options={PERIOD_OPTIONS}
        selectedValues={selectedPeriod}
        onSelect={(label) => setSelectedPeriod(label)}
      />

      <FilterModal
        isOpen={metricModalOpen}
        onClose={() => setMetricModalOpen(false)}
        title={t('charts.cargoVarieties.selectUnit')}
        options={[t('charts.cargoVarieties.boxes'), t('charts.cargoVarieties.kg')]}
        selectedValues={metricDisplayLabel}
        onSelect={(label) => setSelectedMetric(label === t('charts.cargoVarieties.kg') ? 'weight_kg' : 'boxes')}
      />
    </div>
  );
};

export default CargoVarietiesChart;
